import './style.css';
import { usePackSettingsReducer } from './usePackSettingsReducer';
import { LoadingComponent } from '../../controls';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { LevelDefaults } from '../../types/level';
import { MinMaxSettings, PackSettings } from '../../types/packSettings';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';

const PackSettingsPage = () => {
  const { data, addRecord, mergeUp, mergeDown, update, sync } = usePackSettingsReducer();

  if (data.Loading && data.Records.length === 0) {
    return <LoadingComponent />;
  }

  return (
    <div className="page-pack-settings p-3">
      <Toolbar
        start={<Button onClick={addRecord} label="+ Add" size="small" />}
        end={<Button onClick={sync} label="Save" size="small" />}
      />

      {data.Records.length > 0 && (
        <div className="mt-3">
          <PackSettingsListComponent
            settings={data.Records}
            mergeUp={mergeUp}
            mergeDown={mergeDown}
            update={update}
          />
        </div>
      )}
    </div>
  );
};

export default PackSettingsPage;

interface Props {
  settings: PackSettings[];
  mergeUp: (index: number) => void;
  mergeDown: (index: number) => void;
  update: (index: number, pack: PackSettings) => void;
}

const PackSettingsListComponent = ({ settings, mergeUp, mergeDown, update }: Props) => {
  return (
    <div className="settings-list">
      {settings.map((item, index) => (
        <PackSettingsItemComponent
          key={index}
          setting={item}
          index={index}
          total={settings.length}
          mergeUp={mergeUp}
          mergeDown={mergeDown}
          update={update}
        />
      ))}
    </div>
  );
};

interface ItemProps {
  setting: PackSettings;
  index: number;
  total: number;
  mergeUp: (index: number) => void;
  mergeDown: (index: number) => void;
  update: (index: number, pack: PackSettings) => void;
}

const PackSettingsItemComponent = ({
  setting,
  index,
  total,
  mergeUp,
  mergeDown,
  update,
}: ItemProps) => {
  const { start, end, levelWidth, levelHeight, keywordLength, wordsCount, minSize } = setting;

  const isFirst = index === 0;
  const isLast = index === total - 1;

  const handleNumberChange = (event: InputNumberValueChangeEvent) => {
    const { name, value, min } = event.target;
    const nextValue = value || min || 0;

    (setting as any)[name] = nextValue;
    update(index, setting);
  };

  const handleKeywordChange = (val: MinMaxSettings) => {
    setting.keywordLength = val;
    update(index, setting);
  };
  const handleWordCountChange = (val: MinMaxSettings) => {
    setting.wordsCount = val;
    update(index, setting);
  };
  const handleMinSizeChange = (val: MinMaxSettings) => {
    setting.minSize = val;
    update(index, setting);
  };

  return (
    <Card className="settings-card mb-3">
      <div className="settings-card-header">
        <div className="settings-card-title">
          <span className="font-bold text-lg">
            Levels {start} - {end}
          </span>
          <span className="text-sm text-color-secondary">({end - start + 1} levels)</span>
        </div>
        <div className="settings-card-actions">
          <Button
            tooltip="Merge up"
            icon="pi pi-angle-double-up"
            disabled={isFirst}
            onClick={() => mergeUp(index)}
            rounded
            text
            size="small"
          />
          <Button
            tooltip="Merge down"
            icon="pi pi-angle-double-down"
            disabled={isLast}
            onClick={() => mergeDown(index)}
            rounded
            text
            size="small"
          />
        </div>
      </div>

      <div className="settings-card-content">
        <div className="grid">
          <div className="col-12 md:col-6 lg:col-3">
            <div className="settings-group">
              <label className="settings-group-label">Level Range</label>
              <div className="settings-group-fields">
                <div className="settings-field">
                  <label htmlFor={`start${index}`} className="settings-field-label">
                    Start
                  </label>
                  <InputNumber
                    disabled={true}
                    inputClassName="w-full text-center"
                    inputId={`start${index}`}
                    value={start}
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor={`end${index}`} className="settings-field-label">
                    End
                  </label>
                  <InputNumber
                    name="end"
                    inputClassName="w-full text-center"
                    inputId={`end${index}`}
                    value={end}
                    onValueChange={handleNumberChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 md:col-6 lg:col-3">
            <div className="settings-group">
              <label className="settings-group-label">Level Size</label>
              <div className="settings-group-fields">
                <div className="settings-field">
                  <label htmlFor={`levelWidth${index}`} className="settings-field-label">
                    Width
                  </label>
                  <InputNumber
                    name="levelWidth"
                    inputClassName="w-full text-center"
                    inputId={`levelWidth${index}`}
                    min={LevelDefaults.MinLevelSize}
                    max={LevelDefaults.MaxLevelSize}
                    value={levelWidth}
                    onValueChange={handleNumberChange}
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor={`levelHeight${index}`} className="settings-field-label">
                    Height
                  </label>
                  <InputNumber
                    name="levelHeight"
                    inputClassName="w-full text-center"
                    inputId={`levelHeight${index}`}
                    min={LevelDefaults.MinLevelSize}
                    max={LevelDefaults.MaxLevelSize}
                    value={levelHeight}
                    onValueChange={handleNumberChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 md:col-6 lg:col-3">
            <div className="settings-group">
              <label className="settings-group-label">Keyword Length</label>
              <div className="settings-group-fields">
                <div className="settings-field">
                  <label htmlFor={`keywordLength.min${index}`} className="settings-field-label">
                    Min
                  </label>
                  <InputNumber
                    name="keywordLength.min"
                    inputClassName="w-full text-center"
                    inputId={`keywordLength.min${index}`}
                    min={LevelDefaults.MinKeywordLength}
                    max={keywordLength.max}
                    value={keywordLength.min}
                    onValueChange={(e) => {
                      const { value, min } = e.target;
                      handleKeywordChange({
                        ...keywordLength,
                        min: value || min || 0,
                      });
                    }}
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor={`keywordLength.max${index}`} className="settings-field-label">
                    Max
                  </label>
                  <InputNumber
                    name="keywordLength.max"
                    inputClassName="w-full text-center"
                    inputId={`keywordLength.max${index}`}
                    min={keywordLength.min}
                    max={LevelDefaults.MaxKeywordLength}
                    value={keywordLength.max}
                    onValueChange={(e) => {
                      const { value, min } = e.target;
                      handleKeywordChange({
                        ...keywordLength,
                        max: value || min || 0,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 md:col-6 lg:col-3">
            <div className="settings-group">
              <label className="settings-group-label">Words Count</label>
              <div className="settings-group-fields">
                <div className="settings-field">
                  <label htmlFor={`wordsCount.min${index}`} className="settings-field-label">
                    Min
                  </label>
                  <InputNumber
                    name="wordsCount.min"
                    inputClassName="w-full text-center"
                    inputId={`wordsCount.min${index}`}
                    min={LevelDefaults.MinWordsCount}
                    max={wordsCount.max}
                    value={wordsCount.min}
                    onValueChange={(e) => {
                      const { value, min } = e.target;
                      handleWordCountChange({
                        ...wordsCount,
                        min: value || min || 0,
                      });
                    }}
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor={`wordsCount.max${index}`} className="settings-field-label">
                    Max
                  </label>
                  <InputNumber
                    name="wordsCount.max"
                    inputClassName="w-full text-center"
                    inputId={`wordsCount.max${index}`}
                    min={wordsCount.min}
                    max={LevelDefaults.MaxWordsCount}
                    value={wordsCount.max}
                    onValueChange={(e) => {
                      const { value, min } = e.target;
                      handleWordCountChange({
                        ...wordsCount,
                        max: value || min || 0,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 md:col-6 lg:col-3">
            <div className="settings-group">
              <label className="settings-group-label">Word Size</label>
              <div className="settings-group-fields">
                <div className="settings-field">
                  <label htmlFor={`wordSize.min${index}`} className="settings-field-label">
                    Min
                  </label>
                  <InputNumber
                    name="wordSize.min"
                    inputClassName="w-full text-center"
                    inputId={`wordSize.min${index}`}
                    min={LevelDefaults.MinWordSize}
                    max={minSize.max}
                    value={minSize.min}
                    onValueChange={(e) => {
                      const { value, min } = e.target;
                      handleMinSizeChange({
                        ...minSize,
                        min: value || min || 0,
                      });
                    }}
                  />
                </div>
                <div className="settings-field">
                  <label htmlFor={`wordSize.max${index}`} className="settings-field-label">
                    Max
                  </label>
                  <InputNumber
                    name="wordSize.max"
                    inputClassName="w-full text-center"
                    inputId={`wordSize.max${index}`}
                    min={minSize.min}
                    max={LevelDefaults.MaxWordSize}
                    value={minSize.max}
                    onValueChange={(e) => {
                      const { value, min } = e.target;
                      handleMinSizeChange({
                        ...minSize,
                        max: value || min || 0,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
